import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * A dynamic import can fail after a deploy when the requested chunk filename no
 * longer exists (content-hash renamed) — the SPA server then serves index.html for
 * the missing `.js`, which surfaces as one of these browser-specific messages:
 *   - Chrome/Edge: "Failed to fetch dynamically imported module"
 *   - Safari:      "Importing a module script failed"
 *   - Safari/strict: "'text/html' is not a valid JavaScript MIME type"
 * We deliberately do NOT match a bare "Load failed"/"Failed to fetch" so generic
 * network errors don't trigger a reload.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /dynamically imported module|module script|mime type/i;

export const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as { message?: string } | null)?.message ?? '';
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
};

/**
 * Records a one-time reload attempt for `key`, returning true only when this is the
 * first attempt AND it could be persisted. Returns false when already attempted, or
 * when storage is unavailable/throws (Safari private mode, blocked storage). In the
 * storage-failure case we deliberately do NOT reload — a genuinely-missing chunk could
 * otherwise loop forever — and instead let the error fall through to the boundary.
 * Mirrors the guarded storage access in utils/auth/anonymousId.ts.
 */
const shouldReloadOnce = (key: string): boolean => {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  try {
    if (sessionStorage.getItem(key)) {
      return false;
    }
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
};

/**
 * Wraps a dynamic-import factory so a stale-chunk failure triggers a one-time reload
 * (to fetch the fresh index.html + chunk manifest) instead of crashing into the
 * Suspense error boundary. A per-chunk sessionStorage guard prevents a reload loop:
 * if the chunk is still missing after the reload (genuinely gone, not just stale),
 * the error propagates so the boundary can render. Exported separately from
 * `lazyWithReload` so the recovery logic can be unit-tested without rendering.
 */
export const reloadingImport =
  <T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    chunkName: string
  ) =>
  (): Promise<{ default: T }> =>
    importFn().catch((error: unknown) => {
      if (
        isChunkLoadError(error) &&
        shouldReloadOnce(`chunk-reload:${chunkName}`)
      ) {
        window.location.reload();
        // Hold Suspense pending through the reload so the error screen never flashes.
        return new Promise<{ default: T }>(() => undefined);
      }
      throw error;
    });

/**
 * Drop-in replacement for React.lazy that recovers from a stale-chunk import failure
 * by reloading once. `chunkName` keys the per-chunk reload guard.
 */
export const lazyWithReload = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  chunkName: string
): LazyExoticComponent<T> => lazy(reloadingImport(importFn, chunkName));

/**
 * Vite dispatches `vite:preloadError` when its dynamic-import preload helper fails to
 * load a chunk. Registering this catches dynamic imports not wrapped by
 * `lazyWithReload` (route preloads, the Braze SDK, etc.) with the same one-time
 * staleness recovery.
 */
export const listenForChunkPreloadErrors = (): void => {
  window.addEventListener('vite:preloadError', () => {
    if (shouldReloadOnce('chunk-reload:preload')) {
      window.location.reload();
    }
  });
};
